import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";

/**
 * People: faces, rows, and the head-of-queue fact.
 *
 * Part of the widget primitive vocabulary — see ./index.ts. Widgets differ in
 * what they measure, not in how a number, a chart or a person-row looks, so
 * every shape lives here rather than being re-invented per widget.
 */

/**
 * The people behind a number.
 *
 * A count answers "how many"; faces answer "who", which is the question anyone
 * actually has about five people being off today. Overflow is spelled out as a
 * labelled pill rather than a bare `+3` circle — at a glance "+3 others" reads as
 * more people, while another circle in the row reads as another face.
 *
 * `showOverflowLabel` turns the pill back into a compact circle for the layouts
 * that already list every name underneath, where a pill claiming "+2 others"
 * would contradict the list directly below it.
 */
export const AvatarStack = ({
  people,
  max = 3,
  overflowNoun,
  showOverflowLabel = true,
  overflowCount,
  size = "md",
  isLoading,
}: {
  people: { id: number; name: string; image: string | null }[];
  max?: number;
  overflowNoun?: string;
  showOverflowLabel?: boolean;
  /**
   * How many people are NOT shown, when the caller knows better than this
   * component does. List endpoints here are capped, so `people` is often a page
   * rather than the whole set and the overflow implied by its length would
   * understate the truth — badly, when the cap is 6 and the total is 40.
   */
  overflowCount?: number;
  size?: "sm" | "md";
  isLoading?: boolean;
}) => {
  const { t } = useTranslation("company");
  const noun = overflowNoun ?? t("company:widgets.others");
  const avatarClass = size === "sm" ? "size-6" : "size-7";
  const overlapClass = size === "sm" ? "-ml-1.5" : "-ml-2";

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex">
          {Array.from({ length: Math.min(max, 3) }).map((_, index) => (
            <Skeleton
              key={index}
              className={cn(
                avatarClass,
                overlapClass,
                "rounded-full ring-2 ring-surface first:ms-0",
              )}
            />
          ))}
        </div>
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
    );
  }

  if (people.length === 0) return null;

  const shown = people.slice(0, max);
  const overflow = overflowCount ?? people.length - shown.length;

  return (
    <div
      className="flex min-w-0 items-center gap-1.5"
      role="img"
      aria-label={
        overflow > 0
          ? t("company:widgets.names_and_n_more", {
              names: shown.map((person) => person.name).join(", "),
              count: overflow,
              noun,
            })
          : shown.map((person) => person.name).join(", ")
      }
    >
      <div className="flex shrink-0">
        {shown.map((person) => (
          <Avatar
            key={person.id}
            // Native tooltip rather than a Radix one: this renders up to five
            // times per widget across a canvas of ten, and the name is a nicety,
            // not something worth that many portals for.
            title={person.name}
            className={cn(
              avatarClass,
              overlapClass,
              "ring-2 ring-surface first:ms-0",
            )}
          >
            <AvatarImage src={person.image ?? undefined} alt="" />
            <AvatarFallback className="bg-muted text-muted-foreground">
              <User size={size === "sm" ? 11 : 12} aria-hidden />
            </AvatarFallback>
          </Avatar>
        ))}
      </div>

      {overflow > 0 &&
        (showOverflowLabel ? (
          <span className="shrink-0 truncate rounded-full bg-muted px-2 py-1 text-[11px] leading-none font-semibold text-foreground">
            +{overflow} {noun}
          </span>
        ) : (
          <span
            className={cn(
              avatarClass,
              overlapClass,
              "grid shrink-0 place-items-center rounded-full bg-primary-muted text-[10px] font-semibold text-primary ring-2 ring-surface",
            )}
          >
            +{overflow}
          </span>
        ))}
    </div>
  );
};

/** A person row for the list layouts of the larger sizes. */
export const PersonRow = ({
  name,
  image,
  primary,
  secondary,
  to,
  isEditing,
  trailing,
}: {
  name: string;
  image: string | null;
  primary: string;
  secondary?: string | null;
  to?: string;
  isEditing?: boolean;
  trailing?: ReactNode;
}) => {
  const content = (
    <>
      <Avatar className="size-7 shrink-0">
        <AvatarImage src={image ?? undefined} alt={name} />
        <AvatarFallback className="bg-muted text-muted-foreground">
          <User size={12} aria-hidden />
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-foreground">
          {primary}
        </span>
        {secondary && (
          <span className="block truncate text-[11px] text-muted-foreground">
            {secondary}
          </span>
        )}
      </span>
      {trailing}
    </>
  );

  const className =
    "flex items-center gap-2 rounded-lg px-1 py-1 transition-colors";

  if (!to || isEditing) {
    return <div className={className}>{content}</div>;
  }

  return (
    <Link
      to={to}
      className={cn(
        className,
        "outline-hidden hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      {content}
    </Link>
  );
};

/**
 * The head of a queue, stated as a fact.
 *
 * This is the shape that separates a widget from a dashboard tile. A tile
 * answers "how many are waiting"; a widget answers "what is waiting, and since
 * when" — the question you actually have when you glance at your phone. The
 * count does not disappear, it just stops being the headline: it moves to
 * `trailing`, where it is context for the fact rather than a substitute for it.
 *
 * `caption` is where the time anchor belongs ("waiting 3 days", "back Monday",
 * "2 min ago"). A queue head with no sense of age is only half an answer.
 */
export const HeroLine = ({
  avatar,
  primary,
  caption,
  trailing,
  isLoading,
}: {
  /** Optional face — a queue of people reads better with one. */
  avatar?: { name: string; image: string | null };
  primary: string;
  /** A node, not a string, so a self-refreshing <RelativeTime> can live here. */
  caption?: ReactNode;
  trailing?: ReactNode;
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton className="size-8 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-28 rounded" />
          <Skeleton className="h-2.5 w-20 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      {avatar && (
        <Avatar className="size-8 shrink-0">
          <AvatarImage src={avatar.image ?? undefined} alt="" />
          <AvatarFallback className="bg-muted text-muted-foreground">
            <User size={13} aria-hidden />
          </AvatarFallback>
        </Avatar>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] leading-tight font-semibold text-foreground">
          {primary}
        </p>
        {caption && (
          <p className="truncate text-[11px] leading-tight text-muted-foreground">
            {caption}
          </p>
        )}
      </div>
      {trailing && <span className="shrink-0">{trailing}</span>}
    </div>
  );
};
